/**
 * Draft preview for blog posts. Fetches by ID with auth (RLS allows drafts for can_manage_content).
 *
 * NOS-220: mirrors the public BlogPostPage rendering — shared BlogPostHeader
 * (full-bleed cover-image hero or clean header, full meta line), PublicNavBar,
 * and PublicFooter — so the preview matches what visitors will see.
 */

import { useEffect, useMemo, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { supabase } from "@/lib/supabase"
import { useGoogleFont } from "@/hooks/useGoogleFont"
import { useNavigation } from "@/hooks/useNavigation"
import type { NavPageSummary } from "@/hooks/usePublicSiteShell"
import { PublicNavBar } from "@/components/PublicNavBar"
import { parseAnnouncement } from "@/lib/announcement"
import { PublicFooter } from "@/components/public/PublicFooter"
import { BlockRenderer } from "@/components/website-builder/BlockRenderer"
import { BlogPostHeader } from "@/components/blog/BlogPostHeader"
import { calcReadingTime, splitLeadingHero } from "@/lib/blog/postMeta"
import type { OrgCustomizations, PageSection } from "@/templates/types"
import { Button } from "@/components/ui/button"
import { ArrowLeft, Eye } from "lucide-react"
import { useOrgStore } from "@/stores/orgStore"

function contentToSections(content: unknown, orgId: string): PageSection[] {
  const arr = Array.isArray(content) ? content : []
  return (arr as Record<string, unknown>[]).map((item, i) => ({
    id: String(item.id ?? crypto.randomUUID()),
    section_type: String(item.section_type ?? "paragraph"),
    order_index: typeof item.order_index === "number" ? item.order_index : i,
    content: (item.content as Record<string, unknown>) ?? null,
    page_id: "",
    org_id: orgId,
    is_visible: true,
  }))
}

interface BlogPostRow {
  id: string
  org_id: string
  title: string
  slug: string
  excerpt: string | null
  cover_image: string | null
  content_sections: unknown
  category_id: string | null
  is_published: boolean
  published_at: string | null
  updated_at: string | null
}

export function BlogPreviewPage() {
  const { postId } = useParams<{ postId: string }>()
  const { currentOrg } = useOrgStore()
  const orgId = currentOrg?.id ?? null

  const [post, setPost] = useState<BlogPostRow | null>(null)
  const [category, setCategory] = useState<{ name: string; slug: string; color: string | null } | null>(null)
  const [customizations, setCustomizations] = useState<OrgCustomizations | null>(null)
  const [navPages, setNavPages] = useState<NavPageSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  const fontFamily = customizations?.font_family ?? "Inter, sans-serif"
  useGoogleFont(fontFamily)
  const { items: navItems } = useNavigation(orgId)

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!orgId || !postId) {
        setLoading(false)
        setNotFound(true)
        return
      }

      const { data: postData, error: postErr } = await supabase
        .from("blog_posts")
        .select("id, org_id, title, slug, excerpt, cover_image, content_sections, category_id, is_published, published_at, updated_at")
        .eq("id", postId)
        .eq("org_id", orgId)
        .maybeSingle()

      if (cancelled) return
      if (postErr || !postData) {
        setNotFound(true)
        setLoading(false)
        return
      }

      setPost(postData as BlogPostRow)

      const postRow = postData as BlogPostRow
      if (postRow.category_id) {
        const { data: catData } = await supabase
          .from("blog_categories")
          .select("name, slug, color")
          .eq("id", postRow.category_id)
          .maybeSingle()
        if (!cancelled && catData) setCategory(catData as { name: string; slug: string; color: string | null })
      }

      const { data: custData } = await supabase
        .from("org_customizations")
        .select("primary_color, secondary_color, font_family, nav_cta_text, logo_scale, announcement")
        .eq("org_id", orgId)
        .maybeSingle()
      if (!cancelled && custData) setCustomizations(custData as OrgCustomizations)

      const { data: pagesData } = await supabase
        .from("pages")
        .select("id, slug, title")
        .eq("org_id", orgId)
        .eq("is_published", true)
        .order("slug")
      if (!cancelled && pagesData) setNavPages(pagesData as NavPageSummary[])

      setLoading(false)
    }

    load()
    return () => {
      cancelled = true
    }
  }, [orgId, postId])

  const sections = useMemo(
    () => (post ? contentToSections(post.content_sections, post.org_id) : []),
    [post]
  )
  // NOS-220: a leading hero block's presentation is the shared header, so it
  // is dropped from the body (prevents a duplicate title under the header).
  const bodyBlocks = useMemo(() => splitLeadingHero(sections).bodyBlocks, [sections])

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <p className="text-muted-foreground">Loading preview...</p>
      </div>
    )
  }

  if (notFound || !post) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
        <h1 className="text-2xl font-bold">Post not found</h1>
        <Button asChild variant="outline">
          <Link to="/dashboard/blog">Back to Blog</Link>
        </Button>
      </div>
    )
  }

  const cust = customizations ?? {
    primary_color: "#3b82f6",
    secondary_color: "#1e40af",
    font_family: "Inter, sans-serif",
    template_id: null,
  }
  const primaryColor = cust.primary_color ?? "#3b82f6"
  const org = currentOrg
    ? {
        id: currentOrg.id,
        name: currentOrg.name,
        slug: currentOrg.slug,
        mission_statement: (currentOrg.mission_statement as string | null) ?? null,
        logo_url: (currentOrg.logo_url as string | null) ?? null,
      }
    : null
  // Preview links point at the real public site pages for this org.
  const getPagePath = (slug: string) =>
    slug === "home" ? `/${org?.slug ?? ""}` : `/${org?.slug ?? ""}/${slug}`

  return (
    <div
      className="min-h-screen"
      style={
        {
          "--primary-color": primaryColor,
          fontFamily,
        } as React.CSSProperties
      }
    >
      <div className="border-b bg-amber-500/10 px-4 py-2 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-200">
          <Eye className="h-4 w-4" />
          <span>
            {post.is_published ? "Preview" : "Preview – Draft (not visible to visitors)"}
          </span>
        </div>
        <Button variant="outline" size="sm" asChild>
          <Link to={`/dashboard/blog/${postId}/edit`}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to editor
          </Link>
        </Button>
      </div>

      <PublicNavBar
        orgName={org?.name ?? "Organization"}
        logoUrl={org?.logo_url}
        navItems={navItems}
        pages={navPages}
        primaryColor={primaryColor}
        navCtaText={cust.nav_cta_text ?? "Donate"}
        announcement={parseAnnouncement(cust.announcement)}
        fontFamily={fontFamily}
        getPagePath={getPagePath}
        orgId={org?.id}
        logoScale={(cust.logo_scale as number | undefined) ?? 1.0}
        stickyNav={false}
      />

      {/* NOS-220: shared post header — same presentation as the public page */}
      <BlogPostHeader
        title={post.title}
        coverImage={post.cover_image}
        categoryName={category?.name ?? null}
        categoryColor={category?.color ?? null}
        publishedAt={post.published_at}
        updatedAt={post.updated_at}
        readingTimeMinutes={calcReadingTime(sections)}
        primaryColor={primaryColor}
      />

      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
        {/* NOS-156: @container so the render blocks' @sm/@md/@lg variants resolve */}
        {/* NOS-221: no `prose` here — it would restyle every section block now
            that the plugin is installed; ParagraphBlock brings its own `prose`. */}
        <div className="max-w-none @container">
          {bodyBlocks.map((section) => (
            <BlockRenderer
              key={section.id}
              section={section}
              customizations={cust}
              org={org}
            />
          ))}
        </div>
      </main>

      {/* NOS-220: preview shows the site footer like the public post page */}
      {org && (
        <PublicFooter
          orgId={org.id}
          orgName={org.name}
          logoUrl={org.logo_url}
          missionStatement={org.mission_statement}
          primaryColor={primaryColor}
          secondaryColor={cust.secondary_color ?? "#1e40af"}
          fontFamily={fontFamily}
          pages={navPages}
          getPagePath={getPagePath}
          logoScale={(cust.logo_scale as number | undefined) ?? 1.0}
        />
      )}
    </div>
  )
}
